import { ALL_VALUE, INVALID_VALUE } from '../API/ItemsGetters'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'

/**
 * Add-user CXS scope: exactly one concrete permissions partition (not all/none).
 *
 * @param partitionIds - Value of the permissions partition field (array of partition IDs in TelSys).
 * @returns True when CXS subscription may be offered for this scope.
 */
export function isCxsSubscriptionScopeAllowedForAddUser(partitionIds: Nullable<number[]> | undefined): boolean {
    if (!partitionIds || partitionIds.length !== 1) return false

    const partId: number = partitionIds[0]

    return partId !== ALL_VALUE && partId !== INVALID_VALUE
}

/**
 * Whether scope uses exactly one concrete partition.
 *
 * @param partitionIds - Value of the permissions partition field.
 * @returns True when the selected partition is concrete.
 */
export function isCxsSubscriptionScopeAllowedForPermissionsPartition(
    partitionIds: Nullable<number[]> | undefined
): boolean {
    return isCxsSubscriptionScopeAllowedForAddUser(partitionIds)
}

/**
 * Whether the telephony server type supports CXS subscriptions.
 *
 * CX Score is offered for BroadWorks and MS Teams telephony servers only.
 *
 * @param telSysType - Resolved telephony server type for the user.
 * @returns True when CXS subscription may be offered for this telephony server type.
 */
export function isCxsTelSysSupported(telSysType: Nullable<TelSysTypes>): boolean {
    return telSysType === TelSysTypes.BROADSOFT_BROADWORKS || telSysType === TelSysTypes.MS_TEAMS
}

type AddUserCxsApplicabilityPendingArgs = {
    selectedUser?: string
    isCXScoreFeatureEnabled: Nullable<boolean>
    permissionPartitionIds: Nullable<number[]> | undefined
    telSysValue: Nullable<number> | undefined
    telSysType: Nullable<TelSysTypes>
    telSysIntegrationModeOptions?: Nullable<string>
}

/**
 * Whether edit-mode CXS applicability is still being determined.
 *
 * For MS Teams servers the integration type and mode also gate CXS, so applicability stays pending until both
 * have loaded ({@code undefined} means not yet fetched; {@code null} means fetched with no value reported).
 *
 * @param args - Applicability inputs from the add-user modal.
 * @returns True when scope is concrete enough to support CXS, but feature-flag, tel-sys type or Teams
 *     integration data has not loaded yet.
 */
export function isCxsSubscriptionApplicabilityPendingForAddUser({
    selectedUser,
    isCXScoreFeatureEnabled,
    permissionPartitionIds,
    telSysValue,
    telSysType,
    telSysIntegrationModeOptions
}: AddUserCxsApplicabilityPendingArgs): boolean {
    if (!selectedUser) return false

    if (!isCxsSubscriptionScopeAllowedForAddUser(permissionPartitionIds)) return false

    if (!telSysValue || telSysValue === ALL_VALUE || telSysValue === INVALID_VALUE) return false

    if (isCXScoreFeatureEnabled === false) return false

    return (
        isCXScoreFeatureEnabled === undefined ||
        telSysType === TelSysTypes.UNKNOWN ||
        // CXS on MS Teams hinges on the business integration options, which load with the telsys.
        (telSysType === TelSysTypes.MS_TEAMS && telSysIntegrationModeOptions === undefined)
    )
}
